import React, { createContext, useContext } from 'react';
import { useLocalStorage } from '../../common/utils';

/**
 * @description Shares the Shoreline module's light/dark theme preference between `NavigationBar`
 * (where the toggle button lives, since the top bar is shared/rendered once for the whole app) and
 * `Shoreline/result.jsx` (where the theme is actually applied) — two separate components under the
 * same route tree, not parent/child. A plain `useLocalStorage` call in each wouldn't stay in sync
 * live: each would own its own React state, and a `localStorage` write from one doesn't notify the
 * other without a page reload. A single provider holding one shared `useLocalStorage` instance
 * fixes that, following the same context-bridge pattern already used by `SessionExportContext`.
 */
const ThemeModeContext = createContext({ shorelineThemeMode: 'light', toggleShorelineThemeMode: () => {} });

export const ThemeModeProvider = ({ children }) => {
  const [shorelineThemeMode, setShorelineThemeMode] = useLocalStorage('shorelineThemeMode', 'light');

  const toggleShorelineThemeMode = () => {
    setShorelineThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeModeContext.Provider value={{ shorelineThemeMode, toggleShorelineThemeMode }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
